import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import PreviewPDF from '../../PreviewPDF';
import { loadPdfReport, resetInventory } from '../../../../services/inventoryService';
import { useNotification } from '../../../Shared/Notifications/NotificationProvider';
import { NotificationTypes } from '../../../Shared/Notifications/Notification';
import { useExceptionDialog } from '../../../Shared/ExceptionDialog/ExceptionDialogProvider';
import { useLoading } from "../../../Shared/LoadingContext";
import SamplingStatistics from './SamplingStatistics';
import { FormattedMessage } from 'react-intl';
import { on, off, trigger } from '../../../../actions/events'
import { wizardEvents } from '../Wizard';
import ConfirmDialog from '../../../Shared/ConfirmDialog';

const SamplingTabs = ({
    statistics,
    dataOrColumnMappingError,
    importInProgress,
    importFinishedWithError,
    readOnly,
    enablePdfSummary,
    enableReportPreview,
    population,
    isPerpetualInventory,
    setContinueBtnDisabled,
    isHighQuality
}) => {

    const dispatchNotification = useNotification();
    const notifyError = (errorMessage) => dispatchNotification({
        type: NotificationTypes.error,
        pageId: 'pageInventoryWizard.Sampling',
        message: errorMessage
    });

    const dispatchExceptionDialog = useExceptionDialog();
    const showException = error => dispatchExceptionDialog({
        pageId: 'pageInventoryWizard.StockData',
        message: error.detail || error
    });

    const { loading, setLoading } = useLoading();

    const [selected, setSelected] = React.useState(0);
    const [fileInitialImport, setFileInitialImport] = React.useState(null);
    const [fileStructureAnalysis, setFileStructureAnalysis] = React.useState(null);
    const [fileLorenzCurve, setFileLorenzCurve] = React.useState(null);
    const [fileStratification, setFileStratification] = React.useState(null);

    const pricesImported = statistics?.pricesImported;
    const reportTabsVisible = !dataOrColumnMappingError && pricesImported;

    const pdfPreviewErrorCallback = error => {
        notifyError('Unexpected error loading pdf preview');
        showException(error);
    }

    const handleSelect = async e => {
        setSelected(e.selected);
        switch (e.selected) {
            case 1:
                if (!fileInitialImport) {
                    setLoading(true);
                    loadPdfReport(window.enumReportType.InitialImportResult, setFileInitialImport, pdfPreviewErrorCallback, population?.populationId);
                    setLoading(false);
                }
                break;
            case 2:
                if (!fileStructureAnalysis) {
                    setLoading(true);
                    loadPdfReport(window.enumReportType.StructureAnalysis, setFileStructureAnalysis, pdfPreviewErrorCallback, population?.populationId);
                    setLoading(false);
                }
                break;
            case 3:
                if (!fileLorenzCurve) {
                    setLoading(true);
                    loadPdfReport(window.enumReportType.LorenzCurve, setFileLorenzCurve, pdfPreviewErrorCallback, population?.populationId);
                    setLoading(false);
                }
                break;
            case 4:
                setLoading(true);
                setFileStratification(null);
                loadPdfReport(window.enumReportType.Stratification, setFileStratification, pdfPreviewErrorCallback, population?.populationId);
                setLoading(false);
                break;
            default:
        }
    };

    const [isThresholdChanged, setIsThresholdChanged] = useState(false)

    const wizzardContinueClick = React.useCallback(() => {
        if (isThresholdChanged) {
            setConfirmDialogTitle(<FormattedMessage id='SamplingStatistics.ConfirmDialogTitle' />);
            setConfirmDialogDetail(<FormattedMessage id='SamplingStatistics.ConfirmDialogBody' />);
            setConfirmDialogPositive(<FormattedMessage id='Control.Continue' />);
            setConfirmDialogCallback({
                success: () => continueWizzardSteps()
            });
            setIsConfirmDialogVisible(true);
        }
        else {
            continueWizzardSteps()
        }
    }, [isThresholdChanged]);

    const handleWizardPrevious = React.useCallback(() => {
        setConfirmDialogTitle(<FormattedMessage id="ResetInventory.ResetInventory" />);
        setConfirmDialogDetail(<FormattedMessage id="ResetInventory.AreYouSureYouWantToResetThisUpload" />);
        setConfirmDialogPositive(<FormattedMessage id="Control.Reset" />);
        setConfirmDialogCallback({
            success: () => resetInventoryAndGoBack()
        });
        setIsConfirmDialogVisible(true);
    }, []);

    const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false);
    const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
    const [confirmDialogDetail, setConfirmDialogDetail] = useState('');
    const [confirmDialogPositive, setConfirmDialogPositive] = useState('');
    const [confirmDialogCallback, setConfirmDialogCallback] = useState(null);

    const handleConfirmDialogClose = (isConfirmed) => {
        setIsConfirmDialogVisible(false);
        if (confirmDialogCallback) {
            if (isConfirmed) {
                confirmDialogCallback.success();
            }
        }
    };

    React.useEffect(() => {
        on(wizardEvents.continueButtonClicked, wizzardContinueClick);
        on(wizardEvents.previousButtonClicked, handleWizardPrevious);

        return () => {
            off(wizardEvents.continueButtonClicked, wizzardContinueClick);
            off(wizardEvents.previousButtonClicked, handleWizardPrevious);
        };
    }, [wizzardContinueClick, handleWizardPrevious]);

    const continueWizzardSteps = () => {
        trigger(wizardEvents.samplingContinue, {});
    };

    const resetInventoryAndGoBack = () => {
        setLoading(true);
        resetInventory(
            population.populationId,
            () => {
                setLoading(false);
                trigger(wizardEvents.samplingPrevious, {});
            },
            handleError);
    };

    const handleError = (errorMessage, showNotif = true) => {
        setLoading(false);
        if (showNotif) {
            notifyError(errorMessage);
        }
        else {
            showException(errorMessage);
        }
    };

    return <div>
        <TabStrip selected={selected} onSelect={handleSelect}>
            <TabStripTab title={<FormattedMessage id='ImportResult.Title' />}>
                <SamplingStatistics statistics={statistics} importInProgress={importInProgress}
                    setThresholdChanged={setIsThresholdChanged} setContinueBtnDisabled={setContinueBtnDisabled} isHighQuality={isHighQuality}
                    importFinishedWithError={importFinishedWithError} readOnly={readOnly} isPerpetualInventory={isPerpetualInventory} />
            </TabStripTab>
            <TabStripTab title={<FormattedMessage id='General.PdfView' />} disabled={!enablePdfSummary && !dataOrColumnMappingError}>
                <div>
                    <PreviewPDF file={fileInitialImport}
                        prevPageText='<'
                        nextPageText='>'
                        downloadFileText={<FormattedMessage id='Reporting.DownloadDocument' />}
                    />
                </div>
            </TabStripTab>
            {reportTabsVisible &&
                <TabStripTab title={<FormattedMessage id='SamplingStatistics.Struc' />} disabled={!enableReportPreview}>
                    <div>
                        <PreviewPDF file={fileStructureAnalysis}
                            prevPageText='<'
                            nextPageText='>'
                            downloadFileText={<FormattedMessage id='Reporting.DownloadDocument' />}
                        />
                    </div>
                </TabStripTab>
            }
            {reportTabsVisible &&
                <TabStripTab title={<FormattedMessage id='SamplingStatistics.Lorenz' />} disabled={!enableReportPreview}>
                    <div>
                        <PreviewPDF file={fileLorenzCurve}
                            prevPageText='<'
                            nextPageText='>'
                            downloadFileText={<FormattedMessage id='Reporting.DownloadDocument' />}
                        />
                    </div>
                </TabStripTab>
            }
            {reportTabsVisible &&
                <TabStripTab title={<FormattedMessage id='SamplingStatistics.Strat' />} disabled={!enableReportPreview}>
                    <div>
                        <PreviewPDF file={fileStratification}
                            prevPageText='<'
                            nextPageText='>'
                            downloadFileText={<FormattedMessage id='Reporting.DownloadDocument' />}
                        />
                    </div>
                </TabStripTab>
            }
        </TabStrip>
        <ConfirmDialog
            visible={isConfirmDialogVisible}
            onClose={handleConfirmDialogClose}
            negative={<FormattedMessage id='Control.Cancel' />}
            positive={confirmDialogPositive}
            title={confirmDialogTitle}
            detail={confirmDialogDetail}
        />
    </div>;
};

SamplingTabs.propTypes = {
    statistics: PropTypes.object,
    dataOrColumnMappingError: PropTypes.bool,
    importInProgress: PropTypes.bool,
    importFinishedWithError: PropTypes.bool,
    readOnly: PropTypes.bool,
    enablePdfSummary: PropTypes.bool,
    enableReportPreview: PropTypes.bool,
    population: PropTypes.object,
    isPerpetualInventory: PropTypes.bool,
    setContinueBtnDisabled: PropTypes.func,
    isHighQuality: PropTypes.bool
};

export default SamplingTabs;
